import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { CheckCircle, Trash2, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DraftsReviewDialog = ({ open, onClose, onComplete }) => {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [drafts, setDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [discardArmed, setDiscardArmed] = useState(false);

  const fetchDrafts = async () => {
    setLoadingDrafts(true);
    try {
      const res = await axios.get(`${API}/csv/drafts`, { headers: getAuthHeaders(), withCredentials: true });
      setDrafts(res.data.groups || []);
    } catch {
      toast.error(t('toast.failed_to_load_drafts'));
    } finally {
      setLoadingDrafts(false);
    }
  };

  const handleOpen = (isOpen) => {
    if (isOpen) fetchDrafts();
    else onClose();
  };

  const handleFinalize = async (year, source) => {
    try {
      const fd = new FormData();
      fd.append('year', year);
      if (source) fd.append('source', source);
      const res = await axios.post(`${API}/csv/drafts/finalize`, fd, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(res.data.message);
      fetchDrafts();
      onComplete?.();
    } catch {
      toast.error(t('toast.failed_to_finalize_drafts'));
    }
  };

  const handleDiscard = async (year, source) => {
    if (!discardArmed) {
      setDiscardArmed(true);
      toast.info('Click discard again to confirm', { duration: 3000 });
      setTimeout(() => setDiscardArmed(false), 3000);
      return;
    }
    setDiscardArmed(false);
    try {
      const fd = new FormData();
      fd.append('year', year);
      if (source) fd.append('source', source);
      const res = await axios.post(`${API}/csv/drafts/discard`, fd, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(res.data.message);
      fetchDrafts();
      onComplete?.();
    } catch {
      toast.error(t('toast.failed_to_discard_drafts'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="sm:max-w-xl bg-white">
        <DialogHeader>
          <DialogTitle>Draft Transactions</DialogTitle>
          <DialogDescription>Review and finalize imported transactions</DialogDescription>
        </DialogHeader>
        {loadingDrafts ? (
          <div className="py-8 text-center">
            <Sparkles className="w-8 h-8 mx-auto animate-pulse text-gray-400" />
            <p className="text-gray-500 mt-2">Loading drafts...</p>
          </div>
        ) : drafts.length === 0 ? (
          <div className="py-8 text-center">
            <CheckCircle className="w-12 h-12 mx-auto text-emerald-500 mb-2" />
            <p className="text-gray-600">No draft transactions to review</p>
          </div>
        ) : (
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {drafts.map((group, idx) => (
              <div key={idx} className="border rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="font-medium">{group.source?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                    <p className="text-sm text-gray-500">Year: {group.year} • {group.transactions?.length || 0} transactions</p>
                  </div>
                  <Badge variant="secondary">Draft</Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                  <div className="bg-emerald-50 p-2 rounded">
                    <p className="text-xs text-gray-600">Income</p>
                    <p className="font-semibold text-emerald-600">${group.total_income?.toLocaleString()}</p>
                  </div>
                  <div className="bg-red-50 p-2 rounded">
                    <p className="text-xs text-gray-600">Expenses</p>
                    <p className="font-semibold text-red-600">${group.total_expenses?.toLocaleString()}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => handleFinalize(group.year, group.source)} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                    <CheckCircle className="w-3 h-3 mr-1" />Finalize
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDiscard(group.year, group.source)} className="flex-1">
                    <Trash2 className="w-3 h-3 mr-1" />Discard
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
